import { Grid } from '@mui/material';
import { ITimeOffRequests, gridTags } from 'app/models/Placement/PlacementMods';
import React, { useEffect, useState } from 'react';
import { generateRowId, getFormattedTimeOff } from '../../ModificationsUtils';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { getTimeOffGridColumns } from './helper';
import { usePromiseTracker } from 'react-promise-tracker';
import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectEditTimeOffRows,
  selectExpandedModsDialog,
  selectIsEditExistingMod,
  selectIsEditMod,
  selectIsUndoModal,
  selectTimeOffEndDateError,
  selectTimeOffHoursError,
  selectTimeOffStartDateError,
} from 'store/redux-store/placement-mods/selectors';
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { makeStyles } from 'tss-react/mui';
import { isValid } from 'date-fns';

const expandedDetailPanelStyles = makeStyles()({
  expandedGrid: {
    fontSize: '14px',
    '& .MuiDataGrid-main': {
      minHeight: '60px',
    },
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  isErrorCell: {
    border: '1px solid !important',
    borderColor: 'red',
    color: 'red',
  },
});
export const TimeOffGrid = ({ timeOffRequests }: { timeOffRequests: ITimeOffRequests[] }) => {
  const { t } = useTranslation();
  const { classes } = expandedDetailPanelStyles();
  const isExpanded = useSelector(selectExpandedModsDialog);
  const timeOffEditRequests = useSelector(selectEditTimeOffRows);
  const isModEdit = useSelector(selectIsEditMod);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [errors, setErrors] = useState({});
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const [headerCount, setHeaderCount] = useState<number>(0);
  const timeOffStartDateErrors = useSelector(selectTimeOffStartDateError);
  const timeOffEndDateErrors = useSelector(selectTimeOffEndDateError);
  const [columns, setColumns] = useState<any>(
    getTimeOffGridColumns(t, isExpanded, isModEdit, classes, timeOffEditRequests, setErrors),
  );
  const dispatch = useDispatch();
  const { promiseInProgress: isCallingAPI } = usePromiseTracker({ area: 'timeoff-details-api', delay: 0 });
  const [currentSortModel, setCurrentSortModel] = React.useState<any[]>([{ field: 'startDate', sort: 'desc' }]);
  const [selectedIds, setSelectedIds] = useState<any[]>([]);

  const timeOffHoursErrors = useSelector(selectTimeOffHoursError);

  const isUndoModal = useSelector(selectIsUndoModal);

  const isEditExistingMod = useSelector(selectIsEditExistingMod);

  useEffect(() => {
    const requests = isModEdit ? timeOffEditRequests : timeOffRequests;
    const initialColumns = getTimeOffGridColumns(t, isExpanded, isModEdit, classes, requests, setErrors);
    setColumns(initialColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isExpanded, isModEdit, timeOffEditRequests, timeOffRequests]);

  const rightActions = React.useMemo<IXGridBulkActionButton[]>(() => {
    return [
      {
        key: 'add-new-timeoff',
        Icon: AddIcon,
        disabled: !isModEdit,
        onClick: e => {
          addNewTimeOffRow(e);
        },
        tooltipProps: {
          tooltipContent: 'Add New Time Off',
        },
      },
      {
        key: 'delete',
        Icon: DeleteOutlinedIcon,
        disabled: !selectedIds || selectedIds?.length === 0,
        onClick: () => deleteHandler(),
        tooltipProps: {
          tooltipContent: 'Delete',
        },
      },
    ];
  }, [
    isExpanded,
    isModEdit,
    selectedIds,
    timeOffEditRequests,
    timeOffHoursErrors,
    timeOffStartDateErrors,
    timeOffEndDateErrors,
  ]);

  const handleRefreshGrid = () => {
    dispatch(placementModsAction.setGridModError(false));
    dispatch(
      placementModsAction.getTimeOffModDetails({
        placementId: selectedModsDetails?.placementId,
        travelerOptionId: selectedModsDetails?.travelerOptionId,
        newMod: !isEditExistingMod && isModEdit,
      }),
    );
  };

  const addNewTimeOffRow = e => {
    const existingRow = timeOffEditRequests?.slice(0);
    const hoursErrors = timeOffHoursErrors?.slice(0);
    const startDateErrors = timeOffStartDateErrors?.slice(0);
    const endDateErrors = timeOffEndDateErrors?.slice(0);

    const newRow = {
      id: `${generateRowId(selectedModsDetails.placementId)}`,
      placementId: selectedModsDetails?.placementId,
      travelerOptionId: selectedModsDetails?.travelerOptionId,
      placementTimeOffId: null,
      startDate: '',
      endDate: '',
      days: '',
      hours: '',
      cost: '',
      placementTimeOffPayerId: '',
      timestamp: '',
      facilityApprovedBy: '',
      interimPlacementId: null,
      interimTravelerOptionId: null,
      placementTimeStamp: '',
      approvalStatusId: '',
      voidStatus: '',
      assigned: false,
    };
    existingRow.unshift(newRow);
    const hourRow = {
      hours: true,
      id: newRow?.id,
    };
    const startDateRow = {
      startDate: true,
      id: newRow?.id,
    };
    const endDateRow = {
      endDate: true,
      id: newRow?.id,
    };

    hoursErrors.unshift(hourRow);
    startDateErrors.unshift(startDateRow);
    endDateErrors.unshift(endDateRow);
    const formattedData = getFormattedTimeOff(existingRow);
    dispatch(placementModsAction.setTimeOffHoursError(hoursErrors));
    dispatch(placementModsAction.setTimeOffStartDateError(startDateErrors));
    dispatch(placementModsAction.setTimeOffEndDateError(endDateErrors));
    dispatch(placementModsAction.setGridModAltered(true));
    dispatch(placementModsAction.setEditTimeOffRows(formattedData));
  };

  const getWidth = () => {
    if (isCallingAPI || !isExpanded) {
      return '780px';
    } else {
      return null;
    }
  };

  const getHeight = () => {
    if (!isCallingAPI || !isExpanded) {
      return '600px';
    } else {
      return null;
    }
  };

  const deleteHandler = () => {
    const modData = isModEdit ? timeOffEditRequests : timeOffRequests;
    const updatedDataAfterDelete = modData.filter(item => !selectedIds.includes(item.id));
    const formattedData = getFormattedTimeOff(updatedDataAfterDelete);
    const updateHours = timeOffHoursErrors.filter(item => !selectedIds.includes(item.id));
    const updateStartDate = timeOffStartDateErrors.filter(item => !selectedIds.includes(item.id));
    const updateEndDate = timeOffEndDateErrors.filter(item => !selectedIds.includes(item.id));
    dispatch(placementModsAction.setTimeOffStartDateError(updateStartDate));

    dispatch(placementModsAction.setGridModAltered(true));
    dispatch(placementModsAction.setTimeOffEndDateError(updateEndDate));
    dispatch(placementModsAction.setTimeOffHoursError(updateHours));
    dispatch(placementModsAction.setEditTimeOffRows(formattedData));

    setSelectedIds([]);
  };
  const resetColumns = () => {
    const requests = isModEdit ? timeOffEditRequests : timeOffRequests;
    setColumns(getTimeOffGridColumns(t, isExpanded, isModEdit, classes, requests, setErrors));
  };

  const getHeaderCount = timeOffEditRequests => {
    const totalRowCount = timeOffEditRequests?.length;
    const nullRowCount = timeOffEditRequests?.filter(
      each =>
        !isValid(new Date(each?.startDate)) ||
        !isValid(new Date(each?.endDate)) ||
        each?.hours === '' ||
        each?.hours === null,
    )?.length;
    setHeaderCount(totalRowCount - nullRowCount);
  };
  useEffect(() => {
    if (timeOffEditRequests?.length === 0) {
      dispatch(placementModsAction.setGridModError(true));
    }
    if (isModEdit) {
      setCurrentSortModel([]);
    }
    getHeaderCount(isModEdit ? timeOffEditRequests : timeOffRequests);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeOffEditRequests]);

  useEffect(() => {
    if (isUndoModal) {
      dispatch(placementModsAction.setTimeOffStartDateError([]));
      dispatch(placementModsAction.setTimeOffEndDateError([]));
      dispatch(placementModsAction.setTimeOffHoursError([]));
      dispatch(placementModsAction.setGridModError(false));
      dispatch(placementModsAction.setIsUndoModal(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUndoModal]);

  useEffect(() => {
    if (timeOffEditRequests?.length === 0) {
      dispatch(placementModsAction.setGridModError(true));
    } else if (
      timeOffHoursErrors?.some(val => val.hours) ||
      timeOffStartDateErrors.some(val => val.startDate) ||
      timeOffEndDateErrors.some(val => val.endDate)
    ) {
      dispatch(placementModsAction.setGridModError(true));
    } else {
      dispatch(placementModsAction.setGridModError(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeOffHoursErrors, timeOffStartDateErrors, timeOffEndDateErrors, timeOffEditRequests]);

  const TimeOffGrid = React.useMemo(() => {
    return (
      <Grid style={{ paddingBottom: '24px' }}>
        <XGrid
          gridTag={gridTags.TIMEOFFGRIDTAG}
          rows={isModEdit ? getFormattedTimeOff(timeOffEditRequests) || [] : getFormattedTimeOff(timeOffRequests) || []}
          columns={columns}
          disableSelectionOnClick
          pagination={false}
          autoHeight={true}
          hideFooter
          editMode="row"
          inDialog
          title={t('placement.placementMods.timeOff')}
          sortModel={currentSortModel}
          onSortModelChange={model => {
            setCurrentSortModel(model);
          }}
          displayCustomCount={selectedIds?.length > 0 ? selectedIds?.length : headerCount}
          loading={isCallingAPI}
          className={classes.expandedGrid}
          onSelectionModelChange={ids => setSelectedIds(ids)}
          checkboxSelection={isModEdit}
          selectionModel={selectedIds}
          onRowEditStart={() => dispatch(placementModsAction.setIsRowInEditMode(true))}
          onRowEditStop={() => dispatch(placementModsAction.setIsRowInEditMode(false))}
          onCellEditStart={() => dispatch(placementModsAction.setIsRowInEditMode(true))}
          onCellEditStop={() => dispatch(placementModsAction.setIsRowInEditMode(false))}
          toolbarProps={{
            rightActions: rightActions,
            refreshButton: true,
            columnSelector: isModEdit ? false : true,
            resetColumns: () => {
              resetColumns();
              dispatch(
                globalActions.setSnackBar({
                  message: 'Columns have been reset.',
                  severity: 'success',
                }),
              );
            },
            refreshGrid: () => {
              handleRefreshGrid();
            },
          }}
        />
      </Grid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeOffRequests, isExpanded, isCallingAPI, resetColumns, columns, currentSortModel, timeOffEditRequests]);

  return (
    <Grid item xs={12} sx={{ width: getWidth(), height: getHeight() }}>
      {TimeOffGrid}
    </Grid>
  );
};
